import React from 'react';

const EmailQueryDetailsModal = ({ isOpen, onClose, query }) => {
  if (!isOpen || !query) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'new': 'bg-blue-100 text-blue-700 border-blue-200',
      'read': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'replied': 'bg-green-100 text-green-700 border-green-200',
      'archived': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return badges[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getSourceBadge = (source) => {
    const badges = {
      'contact-form': 'bg-purple-100 text-purple-700 border-purple-200',
      'newsletter': 'bg-indigo-100 text-indigo-700 border-indigo-200',
      'other': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return badges[source] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900">Email Query Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Source Badges */}
          <div className="flex gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(query.status)}`}>
              {query.status?.charAt(0).toUpperCase() + query.status?.slice(1) || 'New'}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSourceBadge(query.source)}`}>
              {query.source === 'contact-form' ? 'Contact Form' : query.source?.charAt(0).toUpperCase() + query.source?.slice(1) || 'Contact Form'}
            </span>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">Name</label>
              <p className="text-base text-gray-900">{query.name || 'N/A'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">Email</label>
              <a 
                href={`mailto:${query.email}`}
                className="text-base text-blue-600 hover:text-blue-800 hover:underline"
              >
                {query.email || 'N/A'}
              </a>
            </div>

            {query.phone && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-1 block">Phone</label>
                <a 
                  href={`tel:${query.phone}`}
                  className="text-base text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {query.phone}
                </a>
              </div>
            )}

            {query.company && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-1 block">Company</label>
                <p className="text-base text-gray-900">{query.company}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">Subject</label>
              <p className="text-base text-gray-900">
                {query.subject ? query.subject : <span className="text-gray-400 italic">No subject</span>}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">Message</label>
              <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-base text-gray-900 whitespace-pre-wrap">{query.message || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="border-t border-gray-200 pt-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Submitted:</span>
              <span className="text-gray-900 font-medium">{formatDate(query.createdAt)}</span>
            </div>
            {query.readAt && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Read:</span>
                <span className="text-gray-900 font-medium">{formatDate(query.readAt)}</span>
              </div>
            )}
            {query.repliedAt && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Replied:</span>
                <span className="text-gray-900 font-medium">{formatDate(query.repliedAt)}</span>
              </div>
            )}
            {query.repliedBy && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Replied By:</span>
                <span className="text-gray-900 font-medium">
                  {query.repliedBy.firstName} {query.repliedBy.lastName}
                </span>
              </div>
            )}
          </div>

          {query.notes && (
            <div className="border-t border-gray-200 pt-4">
              <label className="text-sm font-medium text-gray-500 mb-1 block">Admin Notes</label>
              <div className="mt-2 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{query.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailQueryDetailsModal;

